import React from "react";
import styles from "./styles.module.scss";

type BreadcrumbItem = {
  label: string;
  onClick?: () => void;
  href?: string;
  visited?: boolean;
};

interface IBreadCrumbsProps {
  items: BreadcrumbItem[];
  activeIndex?: number; // index of the active page; defaults to last item
  separator?: React.ReactNode; // custom separator
  onNavigate?: (index: number, item: BreadcrumbItem) => void; // global navigation handler
}

const cx = (...classes: Array<string | false | undefined>) =>
  classes.filter(Boolean).join(" ");

const BreadCrumbs: React.FC<IBreadCrumbsProps> = ({
  items,
  activeIndex,
  separator = "/",
  onNavigate,
}) => {
  const resolvedActiveIndex =
    typeof activeIndex === "number"
      ? activeIndex
      : Math.max(items.length - 1, 0);

  const handleClick = (index: number, item: BreadcrumbItem) => {
    if (index === resolvedActiveIndex || !item.visited) return;
    if (item.onClick) {
      item.onClick();
      return;
    }
    if (onNavigate) {
      onNavigate(index, item);
      return;
    }
    if (item.href) {
      window.location.assign(item.href);
    }
  };

  return (
    <nav className={styles.container} aria-label="Breadcrumb">
      <ol className={styles.list}>
        {items.map((item, index) => {
          const isCurrent = index === resolvedActiveIndex;
          return (
            <li key={`${item.label}-${index}`} className={styles.listItem}>
              <button
                type="button"
                className={cx(
                  styles.crumb,
                  isCurrent && styles.active,
                  item.visited && styles.disabled
                )}
                onClick={() => handleClick(index, item)}
                aria-current={isCurrent ? "page" : undefined}
                disabled={isCurrent || !item.visited}
                title={item.label}>
                {item.label}
              </button>
              {index < items.length - 1 && (
                <span className={styles.separator} aria-hidden>
                  {separator}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default BreadCrumbs;
